import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import InfoTooltip from "../tooltip/InfoTooltip";
import "./DocumentUpload.css";
import {
  deletePurchaseProfileDocs,
  uploadDocsForPurchaseProfile,
} from "@/api/myPurchaseProfile";
import { trackApiCall } from "@/utils/analytics";

const DocumentUpload = ({
  isCoBuyer,
  templateId,
  buyerType,
  docRef,
  setDocumentId,
  uploadedFiles,
  setUploadedFiles,
  setErrorValidate,
  setCoBuyerErrorValidate,
}) => {
  const maxFileSizeMB = 10;
  const fileInputRef = useRef(null);

  const [isDeleting, setIsDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter((file) => {
      const isValidFormat = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
      ].includes(file.type);
      const isValidSize = file.size <= maxFileSizeMB * 1024 * 1024;
      if (!isValidSize) {
        toast.error(
          `File size exceeds the maximum limit of ${maxFileSizeMB}MB`
        );
      }
      return isValidFormat && isValidSize;
    });

    if (validFiles.length) {
      setUploading(true);
      try {
        for (const file of validFiles) {
          const response = await handleUpload(file);
          if (response?.status === "SUCCESS" && response?.data?.documentId) {
            trackApiCall({
              eventName: "bid_doc_upload",
              eventCategory: "my-shopper",
              eventSource: "on doc upload",
              eventLabel: "SRC : NewBidWizard",
              eventParams: { doc_type: isCoBuyer ? "llc_cobuyer" : "llc_buyer" },
            });

            toast.success("File uploaded successfully");
            setDocumentId((prev) => [...prev, response?.data?.documentId]);
            setUploadedFiles((prev) => [...prev, response?.data]);
            isCoBuyer
              ? setCoBuyerErrorValidate((prev) => {
                  const updatedError = { ...prev.errors };
                  delete updatedError["coBuyerDocumentId"];
                  return {
                    isValid: true,
                    errors: updatedError,
                  };
                })
              : setErrorValidate((prev) => {
                  const updatedError = { ...prev.errors };
                  delete updatedError["buyerDocumentId"];
                  return {
                    isValid: true,
                    errors: updatedError,
                  };
                });
          } else {
            toast.error("Error uploading files");
          }
        }
      } catch (error) {
        console.error("Error uploading files:", error);
        toast.error("Error uploading files");
      } finally {
        setUploading(false);
        event.target.value = ""; // Clear file input to allow re-uploading same file
      }
    }
  };

  const handleUpload = async (selectedFile) => {
    const formData = new FormData();

    formData.append("file", selectedFile);

    const fileUploadRequest = {
      listingId: null,
      fileCategory: "LLC",
      fileSubCategory: buyerType,
      templateId: templateId,
    };

    const metadataBlob = new Blob([JSON.stringify(fileUploadRequest)], {
      type: "application/json",
    });

    formData.append("fileUploadRequest", metadataBlob);

    return await uploadDocsForPurchaseProfile(formData);
  };

  const handleRemove = async (index, file) => {
    try {
      setIsDeleting(true);
      setDeletingFileId(file?.documentId);
      const deleteResult = await deletePurchaseProfileDocs(file?.documentId);
      if (deleteResult?.status === "SUCCESS" && deleteResult?.data) {
        toast.success("File removed successfully");
        setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
        setDocumentId((prev) => prev.filter((_, i) => i !== index));
      } else {
        toast.error("Error removing file");
      }
    } catch (error) {
      console.log("🚀 ~ DocumentUpload.jsx:71 ~ handleRemove ~ error:", error);
    } finally {
      setIsDeleting(false);
      setDeletingFileId(null);
    }
  };

  return (
    <div className="document-upload-container" ref={docRef}>
      <p className="document-upload-instructions">
        Please upload Business Entity formation documents here (E.g.: LLC -
        Certificate of Formation, Corporation - Articles of Incorporation, LP -
        Certificate of Limited Partnership, etc.)
      </p>

      <div className="document-upload-box">
        <div className="document-upload-title">
          Upload Business Entity Formation Documents <InfoTooltip />
        </div>

        <button
          type="button"
          className="document-upload-browse-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? "Uploading..." : "Browse"}
        </button>

        <input
          type="file"
          ref={fileInputRef}
          multiple
          aria-label="file-upload"
          style={{ display: "none" }}
          accept=".pdf, .jpg, .jpeg"
          onChange={handleFileChange}
        />
      </div>

      <p className="document-upload-format-note">
        File formats accepted: <strong>pdf, jpg, jpeg</strong>. Maximum file
        size allowed is <strong>10MB</strong>.
      </p>

      <div className="document-upload-file-list">
        {uploadedFiles.map((file, index) => (
          <div key={index} className="document-upload-file-item">
            <a
              target="_blank"
              rel="noopener noreferrer"
              className="document-upload-file-link"
            >
              {file?.fileName}
            </a>
            <button
              type="button"
              onClick={() => handleRemove(index, file)}
              className="document-upload-remove-btn"
            >
              {deletingFileId === file?.documentId ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : (
                "Remove"
              )}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocumentUpload;
